/**
 * Order Estimation page — `/stock/estimation` (spec §11.9).
 *
 * Replaces the legacy `pages/EstimationPage.tsx` (which the §15 cleanup
 * deletes). Talks to the new Empirical Bayes Gamma-Poisson forecast
 * endpoint at `POST /api/estimation/forecast` and the
 * `POST /api/estimation/create-draft` companion that turns ticked rows
 * into a Procurement draft.
 *
 * Layout (mirrors the spec's "left/top inputs panel + right/bottom
 * output panel" requirement; on mobile we stack vertically):
 *
 *   - Inputs panel: must_last_until DatePicker, people_change
 *     InputNumber, safety_margin_pct Slider 0..50%, basis_from/to
 *     advanced collapsible (default = last 30 days), include_pending
 *     Switch, exclusion-range chip list (read-only here; "Manage in
 *     settings" link → /settings/estimation-exclusions for CRUD).
 *   - Forecast button → posts the inputs, populates the output panel.
 *   - Output panel: tree-table (Category > Group > Article). Article
 *     rows carry a checkbox + qty InputNumber (defaulted to
 *     `order_qty` from the engine, which is already rounded up to
 *     pack_size). Columns: name, μ ± CI, stock_available,
 *     pending_arrivals, order_qty (editable), badges
 *     (`is_new_article` blue 🆕, `is_low_confidence` yellow ⚠).
 *   - Footer: live-sum total_order_qty + total_order_value_cents from
 *     the ticked rows + edited qtys (server numbers reflect defaults;
 *     once the operator unticks or edits, we recompute client-side).
 *   - "Create draft order" CTA → POST /api/estimation/create-draft →
 *     navigate to /stock/procurement/{procurement_id}.
 *
 * The exclusion list is read from /api/estimation/exclusions on mount
 * and passed verbatim into the forecast body. Operators manage them in
 * settings; this page does NOT mutate them (per spec — the side panel
 * here is "small inline list with a Manage in settings link").
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Empty,
  Popover,
  Row,
  Slider,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";

import { NumInput as InputNumber } from "../../components/NumInput";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { Link, useNavigate } from "react-router-dom";

import {
  ApiError,
  type CategoryRow,
  type ChainRow,
  type CohortContributorOut,
  type CreateDraftLine,
  type EstimationExclusionRow,
  type ForecastExclusionRange,
  type ForecastLineOut,
  type ForecastRequest,
  type ForecastResponse,
  type PfandValueRow,
  type ProductGroupRow,
  api,
} from "../../api/client";
import { formatCents } from "../../utils/dateFormat";

const { Paragraph, Text, Title } = Typography;

// ---------------------------------------------------------------------------
// Tree-row shape for the AntD Table component
// ---------------------------------------------------------------------------

type TreeRow =
  | {
      kind: "category";
      key: string;
      categoryId: number | null;
      name: string;
      children: TreeRow[];
    }
  | {
      kind: "group";
      key: string;
      groupId: number | null;
      categoryId: number | null;
      name: string;
      children: TreeRow[];
    }
  | {
      kind: "article";
      key: string;
      chainRootId: number;
      name: string;
      line: ForecastLineOut;
    };

// Article keys live separately from the row keys so we can drive
// rowSelection without collisions across categories/groups (which use
// their own prefixed keys).
const ARTICLE_KEY = (chainRootId: number) => `article:${chainRootId}`;

// ---------------------------------------------------------------------------
// Hover-popover that explains where a forecast row's prior came from.
// Hangs off the article-name cell; mirrors the operator's question
// "why is this article forecast — what other articles influenced it?"
// ---------------------------------------------------------------------------

function CohortList({ rows }: { rows: CohortContributorOut[] }) {
  if (rows.length === 0) {
    return <Text type="secondary">(empty)</Text>;
  }
  return (
    <ul style={{ paddingLeft: 18, margin: 0 }}>
      {rows.map((r) => (
        <li key={r.chain_root_id} style={{ lineHeight: 1.55 }}>
          <span>{r.article_name}</span>
          {r.is_deleted && (
            <Tag color="red" style={{ marginLeft: 6 }}>
              deleted
            </Tag>
          )}
          <Text type="secondary">
            {" — "}
            {r.basis_qty} sold · {r.distinct_buyers} buyers
          </Text>
        </li>
      ))}
    </ul>
  );
}

function PriorSourcePopover({
  line,
  categoryName,
  groupName,
}: {
  line: ForecastLineOut;
  categoryName: string | null;
  groupName: string | null;
}) {
  const headerLabel = (() => {
    switch (line.prior_source) {
      case "cohort":
        return "Own history";
      case "group":
        return `Group${groupName ? ` '${groupName}'` : ""}`;
      case "category":
        return `Category${categoryName ? ` '${categoryName}'` : ""}`;
      case "unavailable":
        return "No cohort";
      default:
        return line.prior_source;
    }
  })();
  return (
    <div style={{ minWidth: 280 }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong>Prior:</Text> <Text>{headerLabel}</Text>
      </div>
      {line.prior_source === "unavailable" && (
        <Text type="secondary">
          Not enough sales in this product group or category for a pooled
          rate (need at least two siblings with sales) — type a manual qty
          to stock the first event.
        </Text>
      )}
      {line.prior_source === "cohort" && (
        <Text type="secondary">
          μ = (article's basis qty / total person-days on-site in basis) ×
          person-days in target. Non-buyers count in the denominator, so
          low-prevalence articles can't broadcast a buyer rate to everyone.
        </Text>
      )}
      {(line.prior_source === "group" || line.prior_source === "category") && (
        <Text type="secondary">
          No own basis sales. μ inherits the pooled rate of the
          {line.prior_source === "group" ? " product group" : " category"} —
          sum of all siblings' basis qty divided by total person-days.
        </Text>
      )}
      {line.group_cohort.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Text strong>Group{groupName ? ` '${groupName}'` : ""}:</Text>
          <CohortList rows={line.group_cohort} />
        </div>
      )}
      {line.category_cohort.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <Text strong>Category{categoryName ? ` '${categoryName}'` : ""}:</Text>
          <CohortList rows={line.category_cohort} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export const EstimationPage = () => {
  const navigate = useNavigate();

  // -------------------------------------------------------------------
  // Form state
  // -------------------------------------------------------------------
  const [mustLastUntil, setMustLastUntil] = useState<Dayjs | null>(null);
  const [peopleChange, setPeopleChange] = useState<number>(0);
  const [safetyMargin, setSafetyMargin] = useState<number>(10);
  // Substitution share: when the operator unticks an article, what
  // fraction of its forecast μ should redistribute to still-ticked
  // siblings in the same product_group? Pure UI policy — server
  // forecast is independent per chain root. 0 = no substitution
  // (legacy behaviour), 100 = all unticked demand walks to siblings.
  // 70 is a reasonable default for beer / soft-drink venues per FMCG
  // rules-of-thumb; operators can tune per forecast.
  const [substitutionShare, setSubstitutionShare] = useState<number>(70);
  const [basisFrom, setBasisFrom] = useState<Dayjs | null>(
    dayjs().subtract(30, "day"),
  );
  const [basisTo, setBasisTo] = useState<Dayjs | null>(dayjs());
  const [includePending, setIncludePending] = useState<boolean>(true);

  // -------------------------------------------------------------------
  // Side data (catalog tree + exclusions list + pfand-value lookup)
  // -------------------------------------------------------------------
  const [chains, setChains] = useState<ChainRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [groups, setGroups] = useState<ProductGroupRow[]>([]);
  const [exclusions, setExclusions] = useState<EstimationExclusionRow[]>([]);
  const [pfandValues, setPfandValues] = useState<PfandValueRow[]>([]);

  // True once the first forecast result lands. The debounced auto-rerun
  // effect noops until then so the initial-mount manual onForecast() call
  // doesn't race a duplicate fire from the input-change watcher.
  const haveSeedRef = useRef<boolean>(false);

  // -------------------------------------------------------------------
  // Output / async state
  // -------------------------------------------------------------------
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  // chain_root_id → edited qty (client-overridden). Missing key = use
  // the server's order_qty default. Set to 0 (or any non-positive) keeps
  // the row disabled-equivalent (excluded from create-draft).
  const [editedQty, setEditedQty] = useState<Map<number, number>>(new Map());
  // Article keys (chain_root_id) the operator has ticked. Auto-seeded
  // on a chain root's FIRST appearance in a forecast (order_qty > 0
  // and not deleted); subsequent re-forecasts preserve the operator's
  // explicit tick/untick decisions per row — see ``seenRoots``. Kept
  // as a Set for O(1) membership in render + sum.
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  // Chain roots that have appeared in any prior forecast result this
  // session. Used to tell "brand-new article (auto-tick it)" from
  // "previously seen and explicitly unticked by the operator (leave
  // it alone)" across debounced re-forecasts. Held in a ref because
  // it never drives a render — only read+mutated inside ``onForecast``.
  const seenRootsRef = useRef<Set<number>>(new Set());

  const [loading, setLoading] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // True once the parallel side-data load (catalog tree + exclusions +
  // pfand values + operational window) resolves. The "initial forecast"
  // effect below waits on this so it can read the default
  // must_last_until cleanly before firing the first POST.
  const [sideDataReady, setSideDataReady] = useState<boolean>(false);

  // -------------------------------------------------------------------
  // Initial load: catalog tree + exclusions + pfand values + operational
  // window. Seeding precedence for the default Must-last-until:
  //   1. The festival-tagged exclusion's ``from_at`` (= "stock should
  //      last until the festival starts, when strichliste goes offline")
  //   2. The configured take-down end date (absolute window cap)
  //   3. Now + 7 days (fallback)
  // -------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [ch, cats, grps, excl, pfand, opWindow] = await Promise.all([
          api.listArticleChains(),
          api.listCategories(),
          api.listProductGroups({}),
          api.listEstimationExclusions(),
          api.listPfandValues(),
          api.getOperationalWindow().catch(() => null),
        ]);
        if (cancelled) return;
        setChains(ch);
        setCategories(cats);
        setGroups(grps);
        setExclusions(excl);
        setPfandValues(pfand);
        // Seed Must-last-until per the precedence list above.
        setMustLastUntil((current) => {
          if (current) return current;
          const festivalExclusion = excl.find((e) => e.source === "festival");
          if (festivalExclusion) {
            const start = dayjs(festivalExclusion.from_at);
            if (start.isValid() && start.isAfter(dayjs())) {
              return start;
            }
          }
          const takedown = opWindow?.takedown_end_date
            ? dayjs(opWindow.takedown_end_date)
            : null;
          if (takedown && takedown.isValid() && takedown.isAfter(dayjs())) {
            return takedown.endOf("day");
          }
          return dayjs().add(7, "day").endOf("day");
        });
        setSideDataReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // -------------------------------------------------------------------
  // Submit handler — POST /api/estimation/forecast
  // -------------------------------------------------------------------
  const onForecast = useCallback(async () => {
    if (!mustLastUntil) {
      setError("Must last until is required.");
      return;
    }
    if (!mustLastUntil.isAfter(dayjs())) {
      setError("Must last until must be in the future.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const body: ForecastRequest = {
        must_last_until: mustLastUntil.toISOString(),
        people_change: peopleChange,
        safety_margin_pct: safetyMargin,
        basis_from: basisFrom ? basisFrom.toISOString() : null,
        basis_to: basisTo ? basisTo.toISOString() : null,
        include_pending_orders: includePending,
        exclusion_ranges: exclusions.map<ForecastExclusionRange>((e) => ({
          from_at: e.from_at,
          to_at: e.to_at,
        })),
      };
      const result = await api.forecastEstimation(body);
      setForecast(result);
      // Preserve operator edits across debounced re-forecasts:
      //   * editedQty — keep typed values for chain roots still in the
      //     response; drop edits for roots that vanished (e.g. an
      //     article was deleted and order_qty dropped to 0, filtering
      //     it out of the table).
      //   * ticked — keep what was previously ticked (so an explicit
      //     untick survives a slider nudge), AND auto-tick lines
      //     APPEARING FOR THE FIRST TIME this session whose
      //     order_qty > 0 and not is_deleted.
      //   * seenRootsRef — running union of all chain roots that have
      //     ever appeared this session, used as the "first time vs
      //     previously seen" discriminator above.
      const responseIds = new Set(result.per_article.map((l) => l.chain_root_id));
      const prevSeen = seenRootsRef.current;

      setEditedQty((prev) => {
        const next = new Map<number, number>();
        for (const [root, qty] of prev) {
          if (responseIds.has(root)) next.set(root, qty);
        }
        return next;
      });

      setTicked((prev) => {
        const next = new Set<number>();
        for (const line of result.per_article) {
          const id = line.chain_root_id;
          if (line.is_deleted) continue;
          if (prev.has(id)) {
            next.add(id);
          } else if (!prevSeen.has(id) && line.order_qty > 0) {
            next.add(id);
          }
          // else: previously seen but unticked → respect the untick.
        }
        return next;
      });

      const nextSeen = new Set(prevSeen);
      for (const id of responseIds) nextSeen.add(id);
      seenRootsRef.current = nextSeen;
      // Flip the seed flag so the debounced auto-rerun effect starts
      // acting on subsequent input changes (avoids racing the
      // initial-mount manual call below).
      haveSeedRef.current = true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [
    mustLastUntil,
    peopleChange,
    safetyMargin,
    basisFrom,
    basisTo,
    includePending,
    exclusions,
  ]);

  // -------------------------------------------------------------------
  // Initial-mount forecast: fires once after the side-data load resolves
  // and a default Must-last-until has been seeded. The debounced
  // auto-rerun below noops until haveSeedRef flips, so this is the only
  // path that produces the very first forecast.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!sideDataReady) return;
    if (haveSeedRef.current) return;
    if (!mustLastUntil || !mustLastUntil.isAfter(dayjs())) return;
    void onForecast();
    // We intentionally only depend on `sideDataReady` + `mustLastUntil`
    // here. `onForecast` changes whenever any input changes, but for the
    // initial fire we only care that the seed is in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sideDataReady, mustLastUntil]);

  // -------------------------------------------------------------------
  // Debounced auto-rerun on input change. Gated on haveSeedRef so the
  // initial-mount path stays exclusive — first paint, set defaults, run
  // forecast once; from then on, any input edit retriggers after 400ms.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!haveSeedRef.current) return;
    if (!mustLastUntil || !mustLastUntil.isAfter(dayjs())) return;
    const t = setTimeout(() => {
      void onForecast();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mustLastUntil,
    peopleChange,
    safetyMargin,
    basisFrom,
    basisTo,
    includePending,
    exclusions,
  ]);

  // Build a chain_root_id → ChainRow map once so per-row buy-price /
  // category lookups stay O(1).
  const chainByRoot = useMemo(
    () => new Map(chains.map((c) => [c.chain_root_id, c])),
    [chains],
  );

  // -------------------------------------------------------------------
  // Substitution overlay (client-side, per product_group).
  //
  // The engine forecasts each chain root independently. When the
  // operator unticks an article OR edits its order_qty below the
  // would-be-ordered quantity, we treat the uncovered demand as
  // shifting to the still-ticked siblings of its product_group,
  // weighted by their forecasts. ``substitutionShare`` (0-100) decides
  // what fraction of that uncovered demand actually walks to siblings;
  // the rest is lost demand (customer walks away).
  //
  // Result: a per-chain-root ``substitutedMu`` (= line.mu_forecast +
  // substitution boost) plus a derived order_qty / mu_target that the
  // UI uses everywhere instead of the raw line numbers. Manual edits
  // still win for the row's own displayed qty (the operator's typed
  // value is sacred) but they can ALSO drive substitution on siblings
  // if they're below the line's natural order need.
  //
  // Pure UI policy — no server round-trip. Recomputes whenever the
  // operator unticks, edits, or moves the substitution slider.
  // -------------------------------------------------------------------
  const ceilToPack = (qty: number, packSize: number | null): number => {
    if (qty <= 0) return 0;
    if (!packSize || packSize <= 0) return Math.ceil(qty);
    return Math.ceil(qty / packSize) * packSize;
  };

  type SubstitutedLine = {
    mu_forecast: number;
    mu_target: number;
    order_qty: number;          // pack-rounded recommended order
    boost: number;              // qty added by substitution
    boostSources: string[];     // article names whose demand fed the boost
  };

  const substituted = useMemo(() => {
    const out = new Map<number, SubstitutedLine>();
    if (!forecast) return out;
    const share = substitutionShare / 100;

    // Step 1: per-line "uncovered demand" that becomes substitution
    // supply for the group's siblings. Untick = full uncovered;
    // ticked with manual edit BELOW natural need = partial uncovered;
    // ticked with no edit (or edit at-or-above natural) = 0 uncovered.
    const uncoveredByRoot = new Map<number, number>();
    const naturalNeedByRoot = new Map<number, number>();
    for (const line of forecast.per_article) {
      const margin = 1 + safetyMargin / 100;
      const naturalNeed = Math.max(
        0,
        line.mu_forecast * margin - line.stock_available - line.pending_arrivals,
      );
      naturalNeedByRoot.set(line.chain_root_id, naturalNeed);

      const isTicked = ticked.has(line.chain_root_id);
      const edited = editedQty.get(line.chain_root_id);
      let coveredByOrder: number;
      if (!isTicked) {
        coveredByOrder = 0;
      } else if (edited === undefined) {
        // No edit + ticked → server default covers naturalNeed (with
        // pack rounding it may be slightly >, but never less).
        coveredByOrder = naturalNeed;
      } else {
        coveredByOrder = Math.max(0, edited);
      }
      const uncovered = Math.max(0, naturalNeed - coveredByOrder);
      if (uncovered > 0) uncoveredByRoot.set(line.chain_root_id, uncovered);
    }

    // Step 2: group lines by product_group_id. Substitution stays
    // strictly inside a group — a Pils that runs short doesn't feed
    // Helles demand.
    const byGroup = new Map<number, ForecastLineOut[]>();
    for (const line of forecast.per_article) {
      const chain = chainByRoot.get(line.chain_root_id);
      const gid = chain?.product_group_id;
      if (gid == null) continue;
      const arr = byGroup.get(gid);
      if (arr) arr.push(line);
      else byGroup.set(gid, [line]);
    }

    // Step 3: per group, redistribute the uncovered demand of unticked
    // / under-ordered articles onto the ticked siblings, weighted by
    // each sibling's own mu_forecast.
    const boostByRoot = new Map<number, { boost: number; sources: Set<string> }>();
    for (const lines of byGroup.values()) {
      // Receivers = ticked AND have positive mu (no point boosting a
      // never-bought sibling — its mu is 0 and the proportional
      // weighting would degenerate).
      const receivers = lines.filter(
        (l) => ticked.has(l.chain_root_id) && l.mu_forecast > 0,
      );
      if (receivers.length === 0) continue;
      const totalReceiverMu = receivers.reduce(
        (sum, l) => sum + l.mu_forecast,
        0,
      );
      if (totalReceiverMu <= 0) continue;

      // Supply = unticked OR under-ordered siblings in the same group.
      for (const supplier of lines) {
        const supply = uncoveredByRoot.get(supplier.chain_root_id) ?? 0;
        if (supply <= 0) continue;
        if (ticked.has(supplier.chain_root_id) && receivers.some((r) => r.chain_root_id === supplier.chain_root_id)) {
          // Edge case: ticked but under-ordered. Don't boost yourself.
          // Allocate to the OTHER ticked siblings only.
          const otherReceivers = receivers.filter(
            (r) => r.chain_root_id !== supplier.chain_root_id,
          );
          if (otherReceivers.length === 0) continue;
          const otherTotal = otherReceivers.reduce(
            (s, l) => s + l.mu_forecast,
            0,
          );
          if (otherTotal <= 0) continue;
          for (const r of otherReceivers) {
            const give = supply * share * (r.mu_forecast / otherTotal);
            const prev = boostByRoot.get(r.chain_root_id) ?? {
              boost: 0,
              sources: new Set<string>(),
            };
            prev.boost += give;
            prev.sources.add(supplier.article_name);
            boostByRoot.set(r.chain_root_id, prev);
          }
        } else {
          for (const r of receivers) {
            const give = supply * share * (r.mu_forecast / totalReceiverMu);
            const prev = boostByRoot.get(r.chain_root_id) ?? {
              boost: 0,
              sources: new Set<string>(),
            };
            prev.boost += give;
            prev.sources.add(supplier.article_name);
            boostByRoot.set(r.chain_root_id, prev);
          }
        }
      }
    }

    // Step 4: materialise per-line substituted numbers.
    const margin = 1 + safetyMargin / 100;
    for (const line of forecast.per_article) {
      const boostInfo = boostByRoot.get(line.chain_root_id);
      const boost = boostInfo?.boost ?? 0;
      const muSub = line.mu_forecast + boost;
      const muTargetSub = muSub * margin;
      const chain = chainByRoot.get(line.chain_root_id);
      const packSize = chain?.pack_size ?? null;
      const orderQtySub = ceilToPack(
        Math.max(
          0,
          muTargetSub - line.stock_available - line.pending_arrivals,
        ),
        packSize,
      );
      out.set(line.chain_root_id, {
        mu_forecast: muSub,
        mu_target: muTargetSub,
        order_qty: orderQtySub,
        boost,
        boostSources: boostInfo ? Array.from(boostInfo.sources) : [],
      });
    }
    return out;
  }, [
    forecast,
    ticked,
    editedQty,
    substitutionShare,
    safetyMargin,
    chainByRoot,
  ]);

  // -------------------------------------------------------------------
  // Helpers: per-row effective qty (manual edit > substituted estimate)
  // -------------------------------------------------------------------
  const effectiveQty = useCallback(
    (line: ForecastLineOut): number => {
      const override = editedQty.get(line.chain_root_id);
      if (override !== undefined) return override;
      const sub = substituted.get(line.chain_root_id);
      return sub?.order_qty ?? line.order_qty;
    },
    [editedQty, substituted],
  );

  // Pfand-value FK lookup. ChainRow only carries the FK ids
  // (pfand_value_id / crate_pfand_value_id); we resolve them to cents
  // via the separately-loaded pfand_values catalog.
  const pfandCentsById = useMemo(
    () => new Map(pfandValues.map((p) => [p.id, p.cents])),
    [pfandValues],
  );

  // Target window length in days — used by the days-of-cover column to
  // detect rows that will run out before target_end if not ordered.
  // Falls back to 7 days when the operator hasn't picked must_last_until
  // yet (the form auto-runs once that lands, this is just defensive).
  const targetDaysRemaining = useMemo(() => {
    if (!mustLastUntil) return 7;
    const diff = mustLastUntil.diff(dayjs(), "hour", true) / 24;
    return diff > 0 ? diff : 0;
  }, [mustLastUntil]);

  // -------------------------------------------------------------------
  // Compose tree rows from forecast + catalog
  // -------------------------------------------------------------------
  const treeRows: TreeRow[] = useMemo(() => {
    if (!forecast) return [];
    // Group forecast lines by category → group via the catalog.
    type Bucket = {
      categoryId: number | null;
      categoryName: string;
      groups: Map<
        number | null,
        { groupId: number | null; groupName: string; lines: ForecastLineOut[] }
      >;
    };
    const buckets = new Map<number | null, Bucket>();

    // Stable categories first so "Uncategorised" sorts to the bottom.
    const catById = new Map(categories.map((c) => [c.id, c]));
    const grpById = new Map(groups.map((g) => [g.id, g]));

    for (const line of forecast.per_article) {
      // Hide deleted articles whose forecast came out zero — they have
      // no operator action attached (the create-draft guard refuses
      // them anyway). Deleted articles with order_qty > 0 stay
      // visible so the operator sees the anomaly + the deleted badge.
      if (line.is_deleted && line.order_qty <= 0) continue;
      const chain = chainByRoot.get(line.chain_root_id) ?? null;
      const catId = chain?.category_id ?? null;
      const catName = catId !== null
        ? catById.get(catId)?.name ?? "Uncategorised"
        : "Uncategorised";
      const grpId = chain?.product_group_id ?? null;
      const grpName = grpId !== null
        ? grpById.get(grpId)?.name ?? "Ungrouped"
        : "Ungrouped";

      let bucket = buckets.get(catId);
      if (!bucket) {
        bucket = { categoryId: catId, categoryName: catName, groups: new Map() };
        buckets.set(catId, bucket);
      }
      let groupBucket = bucket.groups.get(grpId);
      if (!groupBucket) {
        groupBucket = { groupId: grpId, groupName: grpName, lines: [] };
        bucket.groups.set(grpId, groupBucket);
      }
      groupBucket.lines.push(line);
    }

    // Sort categories alphabetically with Uncategorised pushed to the
    // bottom; same for groups within a category, and articles within a
    // group sort by name.
    const orderedBuckets = [...buckets.values()].sort((a, b) => {
      if (a.categoryId === null && b.categoryId !== null) return 1;
      if (b.categoryId === null && a.categoryId !== null) return -1;
      return a.categoryName.localeCompare(b.categoryName);
    });

    return orderedBuckets.map<TreeRow>((b) => {
      const orderedGroups = [...b.groups.values()].sort((a, b) => {
        if (a.groupId === null && b.groupId !== null) return 1;
        if (b.groupId === null && a.groupId !== null) return -1;
        return a.groupName.localeCompare(b.groupName);
      });
      return {
        kind: "category",
        key: `cat:${b.categoryId ?? "none"}`,
        categoryId: b.categoryId,
        name: b.categoryName,
        children: orderedGroups.map<TreeRow>((g) => ({
          kind: "group",
          key: `cat:${b.categoryId ?? "none"}:grp:${g.groupId ?? "none"}`,
          groupId: g.groupId,
          categoryId: b.categoryId,
          name: g.groupName,
          children: g.lines
            .slice()
            .sort((x, y) => x.article_name.localeCompare(y.article_name))
            .map<TreeRow>((line) => ({
              kind: "article",
              key: ARTICLE_KEY(line.chain_root_id),
              chainRootId: line.chain_root_id,
              name: line.article_name,
              line,
            })),
        })),
      };
    });
  }, [forecast, categories, groups, chainByRoot]);

  // -------------------------------------------------------------------
  // Live totals (sum across ticked rows × edited qty)
  // -------------------------------------------------------------------
  const totals = useMemo(() => {
    if (!forecast) return { qty: 0, cents: 0 };
    let qty = 0;
    let cents = 0;
    for (const line of forecast.per_article) {
      if (!ticked.has(line.chain_root_id)) continue;
      const q = effectiveQty(line);
      if (q <= 0) continue;
      qty += q;
      const buy = chainByRoot.get(line.chain_root_id)?.buy_price_cents ?? null;
      if (buy !== null) cents += q * buy;
    }
    return { qty, cents };
  }, [forecast, ticked, effectiveQty, chainByRoot]);

  // Expected Pfand cost across the ticked rows. Per-unit deposit is
  // resolved via the chain's `pfand_value_id`; crate deposit is applied
  // once per full pack (qty // pack_size). Surfaces in the summary row.
  const tickedPfandCents = useMemo(() => {
    if (!forecast) return 0;
    let total = 0;
    for (const line of forecast.per_article) {
      if (!ticked.has(line.chain_root_id)) continue;
      const qty = effectiveQty(line);
      if (qty <= 0) continue;
      const chain = chainByRoot.get(line.chain_root_id);
      if (!chain) continue;
      const unitPfand =
        chain.pfand_value_id !== null
          ? pfandCentsById.get(chain.pfand_value_id) ?? 0
          : 0;
      const cratePfand =
        chain.crate_pfand_value_id !== null
          ? pfandCentsById.get(chain.crate_pfand_value_id) ?? 0
          : 0;
      const packSize = chain.pack_size ?? 0;
      total += qty * unitPfand;
      if (packSize > 0) total += Math.floor(qty / packSize) * cratePfand;
    }
    return total;
  }, [forecast, ticked, effectiveQty, chainByRoot, pfandCentsById]);

  // -------------------------------------------------------------------
  // Per-row cumulative unit totals for group/category headers.
  // Walks descendant article rows, only counting ticked ones, and
  // bucketises by `unit_type` (liters / grams / count).
  // -------------------------------------------------------------------
  type UnitTotals = Map<string, number>;
  const aggregateUnitTotals = useCallback(
    (rows: TreeRow[]): UnitTotals => {
      const out: UnitTotals = new Map();
      const walk = (r: TreeRow) => {
        if (r.kind === "article") {
          if (!ticked.has(r.chainRootId)) return;
          const qty = editedQty.get(r.chainRootId) ?? r.line.order_qty;
          if (qty <= 0) return;
          const chain = chainByRoot.get(r.chainRootId);
          if (!chain?.unit_type) return;
          const unit = chain.unit_type;
          const size = chain.unit_size ? Number(chain.unit_size) : 1;
          if (!Number.isFinite(size)) return;
          out.set(unit, (out.get(unit) ?? 0) + qty * size);
        } else {
          for (const c of r.children) walk(c);
        }
      };
      for (const r of rows) walk(r);
      return out;
    },
    [ticked, editedQty, chainByRoot],
  );

  // -------------------------------------------------------------------
  // Create draft handler
  // -------------------------------------------------------------------
  const onCreateDraft = useCallback(async () => {
    if (!forecast) return;
    const lines: CreateDraftLine[] = [];
    for (const line of forecast.per_article) {
      if (!ticked.has(line.chain_root_id)) continue;
      const qty = effectiveQty(line);
      if (qty <= 0) continue;
      lines.push({ chain_root_id: line.chain_root_id, qty });
    }
    if (lines.length === 0) {
      setError("Tick at least one article with qty > 0 to create a draft.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const r = await api.createEstimationDraft({ lines });
      navigate(`/stock/procurement/${r.procurement_id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }, [forecast, ticked, effectiveQty, navigate]);

  // -------------------------------------------------------------------
  // AntD Table columns
  // -------------------------------------------------------------------
  const columns: ColumnsType<TreeRow> = useMemo(
    () => [
      {
        title: "Article",
        key: "name",
        render: (_: unknown, row) => {
          if (row.kind === "article") {
            const line = row.line;
            const chain = chainByRoot.get(row.chainRootId);
            const popoverContent = (
              <PriorSourcePopover
                line={line}
                categoryName={chain?.category_name ?? null}
                groupName={chain?.product_group_name ?? null}
              />
            );
            return (
              <Space size={6}>
                <Popover
                  content={popoverContent}
                  trigger={["hover", "click"]}
                  placement="rightTop"
                  overlayStyle={{ maxWidth: 420 }}
                  mouseEnterDelay={0.3}
                >
                  <span style={{ cursor: "help", borderBottom: "1px dotted #aaa" }}>
                    {row.name}
                  </span>
                </Popover>
                {line.is_deleted && (
                  <Tooltip title="This article is disabled in strichliste — its history still informs siblings' priors but it cannot be ordered.">
                    <Tag color="red">deleted</Tag>
                  </Tooltip>
                )}
                {line.prior_source === "unavailable" && (
                  <Tooltip title="Not enough sales in this product group or category to fit a prior (need at least two distinct rate observations) — type a manual qty to stock the first event.">
                    <Tag color="warning">no cohort</Tag>
                  </Tooltip>
                )}
                {line.is_new_article && line.prior_source !== "unavailable" && (
                  <Tooltip title={
                    line.prior_source === "group"
                      ? "Brand-new article — forecast inherits from other articles in the same product group."
                      : line.prior_source === "category"
                        ? "Brand-new article — forecast inherits from other articles in the same category (group cohort was empty)."
                        : "Brand-new article — forecast uses an inherited prior."
                  }>
                    <Tag color="blue">🆕</Tag>
                  </Tooltip>
                )}
                {line.is_low_confidence && (
                  <Tooltip title="Sparse history — wide confidence band. Treat μ as a rough guide.">
                    <Tag color="warning">⚠ low conf.</Tag>
                  </Tooltip>
                )}
              </Space>
            );
          }
          // Group/category header: append a "Σ <total> <unit>" chip
          // bucketised per unit_type so mixed groups (e.g. some L, some
          // g) render multiple chips joined with "·".
          const totalsByUnit = aggregateUnitTotals(row.children);
          const formatted = formatUnitTotals(totalsByUnit);
          return (
            <Space size={6}>
              <Text strong>{row.name}</Text>
              {formatted && <Tag>Σ {formatted}</Tag>}
            </Space>
          );
        },
      },
      {
        title: "μ forecast",
        key: "mu",
        align: "right",
        width: 170,
        render: (_: unknown, row) => {
          if (row.kind !== "article") return null;
          const { mu_forecast, ci_low, ci_high } = row.line;
          const halfWidth = Math.max(
            mu_forecast - ci_low,
            ci_high - mu_forecast,
          );
          const sub = substituted.get(row.chainRootId);
          const boosted = sub && sub.boost > 0.5;
          return (
            <Tooltip
              title={
                boosted
                  ? `Engine μ: ${mu_forecast.toFixed(2)}, 95% CI: ${ci_low.toFixed(2)} – ${ci_high.toFixed(2)}. Boosted by ${sub!.boost.toFixed(2)} from unticked/under-ordered siblings: ${sub!.boostSources.join(", ")}.`
                  : `95% CI: ${ci_low.toFixed(2)} – ${ci_high.toFixed(2)}`
              }
            >
              <span>
                {boosted ? (
                  <>
                    {sub!.mu_forecast.toFixed(2)}
                    <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                      +{sub!.boost.toFixed(1)}
                    </Tag>
                  </>
                ) : (
                  <>
                    {mu_forecast.toFixed(2)}
                    <Text type="secondary"> ± {halfWidth.toFixed(2)}</Text>
                  </>
                )}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: "Stock",
        key: "stock",
        align: "right",
        width: 80,
        render: (_: unknown, row) =>
          row.kind === "article" ? row.line.stock_available : null,
      },
      {
        title: "Pending",
        key: "pending",
        align: "right",
        width: 90,
        render: (_: unknown, row) =>
          row.kind === "article" ? row.line.pending_arrivals : null,
      },
      {
        title: "Days cover",
        key: "cover",
        align: "right",
        width: 130,
        render: (_: unknown, row) => {
          if (row.kind !== "article") return null;
          const sub = substituted.get(row.chainRootId);
          const mu = sub?.mu_forecast ?? row.line.mu_forecast;
          if (mu <= 0 || targetDaysRemaining <= 0) {
            return <Text type="secondary">—</Text>;
          }
          const perDay = mu / targetDaysRemaining;
          if (perDay <= 0) return <Text type="secondary">—</Text>;
          const cover =
            (row.line.stock_available + row.line.pending_arrivals) / perDay;
          const willRunOut = cover < targetDaysRemaining - 0.01;
          return (
            <Tooltip
              title={
                willRunOut
                  ? `Current stock + pending covers ${cover.toFixed(1)} of ${targetDaysRemaining.toFixed(1)} target days at the (sub-boosted) per-day rate. Without an order this article runs out before the target ends.`
                  : `Stock + pending covers ${cover.toFixed(1)} days at the (sub-boosted) per-day rate — past the target end.`
              }
            >
              <Text type={willRunOut ? "danger" : undefined} strong={willRunOut}>
                {cover.toFixed(1)} d
              </Text>
            </Tooltip>
          );
        },
      },
      {
        title: "Order qty",
        key: "qty",
        align: "right",
        width: 170,
        render: (_: unknown, row) => {
          if (row.kind !== "article") return null;
          const value = effectiveQty(row.line);
          const isTicked = ticked.has(row.chainRootId);
          const editable = isTicked && !row.line.is_deleted;
          const isManualOverride = editedQty.has(row.chainRootId);
          return (
            <Space size={4}>
              <InputNumber
                size="small"
                min={0}
                step={row.line.pack_size ?? 1}
                value={value}
                disabled={!editable}
                onChange={(v) => {
                  const next = new Map(editedQty);
                  if (v === null || v === undefined) {
                    next.delete(row.chainRootId);
                  } else {
                    next.set(row.chainRootId, Number(v));
                  }
                  setEditedQty(next);
                }}
                style={{ width: 90 }}
              />
              {isManualOverride && (
                <Tooltip title="Reset to the substituted estimate">
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => {
                      const next = new Map(editedQty);
                      next.delete(row.chainRootId);
                      setEditedQty(next);
                    }}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [
      effectiveQty,
      editedQty,
      ticked,
      aggregateUnitTotals,
      chainByRoot,
      substituted,
      targetDaysRemaining,
    ],
  );

  // -------------------------------------------------------------------
  // rowSelection — only article rows are tickable; cat/group rows show
  // a tri-state via AntD's nested-checkbox behaviour automatically when
  // we provide `checkStrictly={false}` and selectable on every row.
  // -------------------------------------------------------------------
  const articleKeys = useMemo(() => {
    const keys: string[] = [];
    const walk = (rows: TreeRow[]) => {
      for (const r of rows) {
        if (r.kind === "article") keys.push(r.key);
        else walk(r.children);
      }
    };
    walk(treeRows);
    return keys;
  }, [treeRows]);

  const selectedRowKeys = useMemo(
    () =>
      Array.from(ticked).map((chainRootId) => ARTICLE_KEY(chainRootId)),
    [ticked],
  );

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  return (
    <Row gutter={[16, 16]}>
      {/* Inputs panel — left column on desktop, top on mobile. */}
      <Col xs={24} lg={8}>
        <Card>
          <Title level={4} style={{ marginTop: 0 }}>
            Forecast inputs
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Empirical Bayes per-user model. Pick a date you need to be
            stocked through; the forecast computes total consumption to
            that horizon.
          </Paragraph>

          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Text strong>Must last until</Text>
              <div style={{ marginTop: 4 }}>
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="DD.MM.YYYY HH:mm"
                  value={mustLastUntil}
                  onChange={(d) => setMustLastUntil(d)}
                  disabledDate={(d) => d.isBefore(dayjs(), "day")}
                  style={{ width: "100%" }}
                />
              </div>
            </div>

            <div>
              <Text strong>People change</Text>
              <div style={{ marginTop: 4 }}>
                <InputNumber
                  value={peopleChange}
                  onChange={(v) => setPeopleChange(Number(v ?? 0))}
                  style={{ width: "100%" }}
                  placeholder="0"
                />
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Signed integer; positive = arrivals, negative = departures,
                ramped linearly across the horizon.
              </Text>
            </div>

            <div>
              <Text strong>Safety margin: {safetyMargin}%</Text>
              <Slider
                min={0}
                max={50}
                value={safetyMargin}
                onChange={(v) => setSafetyMargin(v)}
                marks={{ 0: "0%", 10: "10%", 25: "25%", 50: "50%" }}
              />
            </div>

            <div>
              <Text strong>Substitution share: {substitutionShare}%</Text>
              <Slider
                min={0}
                max={100}
                value={substitutionShare}
                onChange={(v) => setSubstitutionShare(v)}
                marks={{ 0: "0%", 50: "50%", 70: "70%", 100: "100%" }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                When you untick or under-order an article, this fraction of
                its forecast demand walks to its product-group siblings,
                weighted by their μ. The rest is treated as walk-away.
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Switch
                checked={includePending}
                onChange={setIncludePending}
              />
              <Text>Include pending orders</Text>
            </div>

            <Collapse
              size="small"
              items={[
                {
                  key: "advanced",
                  label: "Advanced: basis range",
                  children: (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div>
                        <Text>From</Text>
                        <DatePicker
                          showTime={{ format: "HH:mm" }}
                          format="DD.MM.YYYY HH:mm"
                          value={basisFrom}
                          onChange={(d) => setBasisFrom(d)}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div>
                        <Text>To</Text>
                        <DatePicker
                          showTime={{ format: "HH:mm" }}
                          format="DD.MM.YYYY HH:mm"
                          value={basisTo}
                          onChange={(d) => setBasisTo(d)}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Defaults to the last 30 days when both are
                        cleared.
                      </Text>
                    </Space>
                  ),
                },
              ]}
            />

            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}
              >
                <Text strong>Exclusion ranges</Text>
                <Link to="/settings/estimation-exclusions">
                  Manage in settings
                </Link>
              </div>
              {exclusions.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No exclusions configured.
                </Text>
              ) : (
                <Space direction="vertical" size={2} style={{ marginTop: 4 }}>
                  {exclusions.map((e) => (
                    <Tag key={e.id} style={{ marginRight: 0 }}>
                      {e.label ?? "(unlabelled)"}: {formatRange(e.from_at, e.to_at)}
                    </Tag>
                  ))}
                </Space>
              )}
            </div>

            <Button
              type="primary"
              size="large"
              block
              loading={loading}
              onClick={() => void onForecast()}
              disabled={!mustLastUntil}
            >
              Forecast
            </Button>
          </Space>
        </Card>
      </Col>

      {/* Output panel — right column on desktop, bottom on mobile. */}
      <Col xs={24} lg={16}>
        <Card>
          <Space size={8} style={{ marginTop: 0 }}>
            <Title level={4} style={{ margin: 0 }}>
              Suggested order
            </Title>
            {/* Inline spinner replaces the previous full-page Empty
                state during recomputes — the prior result stays
                visible so the operator can keep editing. */}
            {loading && forecast && (
              <Tag color="processing" style={{ marginLeft: 4 }}>
                <Spin size="small" style={{ marginRight: 6 }} />
                recomputing…
              </Tag>
            )}
          </Space>

          {error && (
            <Alert
              type="error"
              message={error}
              closable
              showIcon
              style={{ marginBottom: 12, marginTop: 12 }}
              onClose={() => setError(null)}
            />
          )}

          {!forecast && !loading && (
            <Empty
              description={
                sideDataReady
                  ? "Pick Must last until to begin."
                  : "Loading catalog…"
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ marginTop: 12 }}
            />
          )}

          {!forecast && loading && (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <Spin />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Computing forecast…</Text>
              </div>
            </div>
          )}

          {forecast && (
            <>
              <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 12 }}>
                Basis {formatRange(forecast.basis_from, forecast.basis_to)} →
                target {formatRange(forecast.target_start, forecast.target_end)}.
                Tick rows to include in the draft; qty is editable.
              </Paragraph>
              <Table<TreeRow>
                size="small"
                columns={columns}
                dataSource={treeRows}
                pagination={false}
                rowKey="key"
                defaultExpandAllRows
                rowSelection={{
                  selectedRowKeys,
                  checkStrictly: false,
                  // Hide the checkbox on non-article rows so cat/group
                  // rows still trigger the tri-state "select all
                  // descendants" behaviour but visually we keep them
                  // clean (AntD groups don't natively support this so
                  // we leave them visible — see the renderCell hook).
                  renderCell: (checked, row, _index, originNode) => {
                    if (row.kind === "article") {
                      return (
                        <Checkbox
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(ticked);
                            if (e.target.checked) {
                              next.add(row.chainRootId);
                            } else {
                              next.delete(row.chainRootId);
                            }
                            setTicked(next);
                          }}
                        />
                      );
                    }
                    return originNode;
                  },
                  onSelect: (row, selected) => {
                    if (row.kind === "article") return;
                    // Cat / group rows toggle every NON-deleted descendant.
                    const next = new Set(ticked);
                    const walk = (r: TreeRow) => {
                      if (r.kind === "article") {
                        if (r.line.is_deleted) return;
                        if (selected) next.add(r.chainRootId);
                        else next.delete(r.chainRootId);
                      } else {
                        for (const c of r.children) walk(c);
                      }
                    };
                    walk(row);
                    setTicked(next);
                  },
                  onSelectAll: (selected) => {
                    if (!selected) {
                      setTicked(new Set());
                      return;
                    }
                    // Skip deleted lines — their checkboxes are
                    // disabled and the route would refuse them on
                    // create-draft anyway.
                    setTicked(
                      new Set(
                        forecast.per_article
                          .filter((l) => !l.is_deleted)
                          .map((l) => l.chain_root_id),
                      ),
                    );
                  },
                  getCheckboxProps: (row) =>
                    row.kind === "article"
                      ? row.line.is_deleted
                        ? { disabled: true }
                        : {}
                      : { name: row.name },
                }}
                summary={() => {
                  if (articleKeys.length === 0) return null;
                  // Two rows: top = ticked indicator across full width
                  // (was previously squeezed into the first cell and
                  // wrapped awkwardly on narrow viewports); bottom = the
                  // right-aligned totals strip with optional Pfand.
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={5}>
                          <Text type="secondary">
                            <Badge
                              count={ticked.size}
                              showZero
                              style={{ backgroundColor: "#1677ff" }}
                            />{" "}
                            of {forecast.per_article.length} articles ticked
                          </Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={5}>
                          <div style={{ textAlign: "right" }}>
                            <Text type="secondary">Total qty: </Text>
                            <Text strong>{totals.qty}</Text>
                            <Text type="secondary" style={{ marginLeft: 16 }}>
                              Order value:{" "}
                            </Text>
                            <Text strong>{formatCents(totals.cents)}</Text>
                            {tickedPfandCents > 0 && (
                              <Text
                                type="secondary"
                                style={{ marginLeft: 8, fontSize: 12 }}
                              >
                                (+ {formatCents(tickedPfandCents)} Pfand)
                              </Text>
                            )}
                            {tickedPfandCents > 0 && (
                              <>
                                <Text
                                  type="secondary"
                                  style={{ marginLeft: 16 }}
                                >
                                  Total with Pfand:{" "}
                                </Text>
                                <Text strong>
                                  {formatCents(totals.cents + tickedPfandCents)}
                                </Text>
                              </>
                            )}
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Button
                  type="primary"
                  size="large"
                  loading={creating}
                  onClick={() => void onCreateDraft()}
                  disabled={ticked.size === 0}
                >
                  Create draft order
                </Button>
              </div>
            </>
          )}
        </Card>
      </Col>
    </Row>
  );
};

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function formatRange(from: string, to: string): string {
  const f = dayjs(from);
  const t = dayjs(to);
  if (!f.isValid() || !t.isValid()) return `${from} → ${to}`;
  return `${f.format("DD.MM HH:mm")} → ${t.format("DD.MM HH:mm")}`;
}

/**
 * Render a "Σ N L · M g" style chip from the bucketed unit totals.
 * Returns "" when there's nothing to show so the caller can skip the
 * Tag entirely.
 */
function formatUnitTotals(totals: Map<string, number>): string {
  if (totals.size === 0) return "";
  return Array.from(totals.entries())
    .map(([unit, mag]) => {
      const label = unit === "liters" ? "L" : unit === "grams" ? "g" : "";
      const rounded = Math.round(mag * 100) / 100;
      return label ? `${rounded} ${label}` : `${rounded}`;
    })
    .join(" · ");
}

export default EstimationPage;
